import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { OnboardingFacade } from '@zero/onboarding/domain';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OnboardingStepGuard {
  protected readonly facade = inject(OnboardingFacade);
  protected readonly router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  public canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkStepAllowed(state.url);
  }

  public canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkStepAllowed(state.url);
  }

  public canLoad(route: Route): Observable<boolean> {
    return this.checkStepAllowed(route.path ?? '');
  }

  public checkStepAllowed(url: string): Observable<boolean> {
    if (!url) {
      return of(false);
    }

    const currentStep = this.facade.getStepByRoute(url.split('?')[0]);
    this.facade.setStep(currentStep);
    if (currentStep < 5) {
      return of(true);
    }

    return combineLatest([
      this.facade.onboardingData$,
      this.facade.onboardingDataLoading$,
      this.facade.isPhoneNumberVerified$,
      this.facade.isPhoneNumberVerifiedLoading$,
      this.facade.doInitialDepositLater$,
    ]).pipe(
      filter(
        ([onboardingData, onboardingDataLoading, , isPhoneNumberVerifiedLoading]) =>
          !onboardingDataLoading && (!onboardingData?.cMobileNr || !isPhoneNumberVerifiedLoading),
      ),
      takeUntilDestroyed(this.#destroyRef),
      map(([onboardingData, , isPhoneNumberVerified, , doInitialDepositLater]) =>
        this.facade.getNextRouteBasedOnData(onboardingData, isPhoneNumberVerified, doInitialDepositLater),
      ),
      map((nextRoute: string) => this.facade.getStepByRoute(nextRoute)),
      tap((step) => currentStep > step && this.facade.goToStep(step)),
      map((step: number) => currentStep <= step),
    );
  }
}
